import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { finalize } from 'rxjs';

import { ApContentService } from '@ap/content';
import { ErrorMessages } from '@ap/form/errors';
import { AuthenticatiebeheerResource } from '@ap/interface-apic-authenticatiebeheer';
import { ErrorCode } from '@ap/interface-apic-authenticatie-fido';

import content from './digicode-herstel.content.json';
import { ChangeDigicodeRequest, ChangeDigicodeResponse, DigicodeHerstelNotification } from './digicode-herstel.model';
import { DigicodeHerstelService } from './digicode-herstel.service';

/**
 * @module DigicodeHerstelModule
 * @ngdoc component
 * @name DigicodeHerstelComponent
 */
@Component({ selector: 'klant-digicode-herstel', templateUrl: 'digicode-herstel.component.html', standalone: false })
export class DigicodeHerstelComponent implements OnInit {
    @Input()
    public changeUsername: boolean;

    @Input()
    public isProspect: boolean;

    @Input()
    public username: string;

    @Output()
    public toLogin: EventEmitter<DigicodeHerstelNotification> = new EventEmitter();

    @Output()
    public notification: EventEmitter<DigicodeHerstelNotification> = new EventEmitter();

    public usernameForm: UntypedFormGroup;
    public passwordForm: UntypedFormGroup;
    public actionId: string;
    public buttonIsDisabled = false;
    public errorMessagesNewUserName: ErrorMessages;
    public errorMessagesCurrentPassword: ErrorMessages;
    public errorMessagesNewPassword: ErrorMessages;
    public errorMessagesValidatePassword: ErrorMessages;

    private readonly usernameRegex = '^([a-zA-Z0-9.@_-]{0,64})?$';
    private readonly baseUrl = '/lsm';

    public constructor(
        private readonly authenticationResource: AuthenticatiebeheerResource,
        private readonly contentService: ApContentService,
        private readonly httpClient: HttpClient
    ) {}

    public ngOnInit(): void {
        this.contentService.addContentItems(content);

        if (!this.username) {
            this.cancel();
            return;
        }
        this.contentService.addContentItems(content);

        this.errorMessagesNewUserName = {
            required: this.contentService.get('klant.digicodeHerstel.newUsername.error.required'),
            illegalCharacters: this.contentService.get('klant.digicodeHerstel.newUsername.error.illegalCharacters'),
            minlength: this.contentService.get('klant.digicodeHerstel.newUsername.error.minlength'),
            maxlength: this.contentService.get('klant.digicodeHerstel.newUsername.error.maxlength'),
            notOnlyDigits: this.contentService.get('klant.digicodeHerstel.newUsername.error.notOnlyDigits'),
            notUnique: this.contentService.get('klant.digicodeHerstel.newUsername.error.notUnique'),
            usernameNotEqualToCurrent: this.contentService.get('klant.digicodeHerstel.newUsername.error.usernameNotEqualToCurrent')
        };

        this.errorMessagesCurrentPassword = {
            lastTry: this.contentService.get('klant.digicodeHerstel.currentPassword.error.lastTry'),
            notCorrect: this.contentService.get('klant.digicodeHerstel.currentPassword.error.notCorrect'),
            required: this.contentService.get('klant.digicodeHerstel.currentPassword.error.required')
        };

        this.errorMessagesNewPassword = {
            required: this.contentService.get('klant.digicodeHerstel.newPassword.error.required'),
            minlength: this.contentService.get('klant.digicodeHerstel.newPassword.error.minlength'),
            maxlength: this.contentService.get('klant.digicodeHerstel.newPassword.error.maxlength'),
            passwordRequirements: this.contentService.get('klant.digicodeHerstel.newPassword.error.passwordRequirements'),
            passwordsNotEqualToCurrent: this.contentService.get('klant.digicodeHerstel.newPassword.error.passwordsNotEqualToCurrent'),
            illegalCharacters: this.contentService.get('klant.digicodeHerstel.newPassword.error.illegalCharacters')
        };

        this.errorMessagesValidatePassword = {
            required: this.contentService.get('klant.digicodeHerstel.validatePassword.error.required'),
            passwordsMatch: this.contentService.get('klant.digicodeHerstel.validatePassword.error.passwordsMatch')
        };

        if (this.changeUsername) {
            this.usernameForm = DigicodeHerstelService.getUsernameForm();
        }
        this.passwordForm = DigicodeHerstelService.getPasswordForm();
    }

    public getErrorMessage(fieldName: string, errorType: string): string {
        return this.contentService.get(`klant.digicodeHerstel.${fieldName}.error.${errorType}`);
    }

    public sendCredentials(): void {
        this.buttonIsDisabled = true;
        this.notification.emit(null);

        if (this.formValid()) {
            const changeDigicodeRequest = this.getChangeDigicodeRequest();

            if (this.isProspect) {
                this.changeProspectDigicode(changeDigicodeRequest);
            } else {
                this.changeDigicode(changeDigicodeRequest);
            }
        } else {
            this.buttonIsDisabled = false;
        }
    }

    public handleErrorCode(httpErrorResponse: HttpErrorResponse): void {
        if (httpErrorResponse.error && httpErrorResponse.error.code) {
            switch (httpErrorResponse.error.code) {
                case ErrorCode.INVALID_CREDENTIALS:
                    this.passwordForm.get('currentPassword').setValue(null);
                    this.passwordForm.get('currentPassword').setErrors({ notCorrect: true });
                    break;
                case ErrorCode.TOKEN_BLOCKED:
                    this.toLogin.emit({
                        status: 'error',
                        message: this.contentService.get(
                            'klant.digicodeHerstel.notification.credentialsChangeFail.tokenBlockedForToday.text'
                        )
                    });
                    break;
                case ErrorCode.TOKEN_BLOCKED_PERMANENTLY:
                    this.toLogin.emit({
                        status: 'error',
                        message: this.contentService.get(
                            'klant.digicodeHerstel.notification.credentialsChangeFail.tokenBlockedPermanently.text'
                        )
                    });
                    break;
                case ErrorCode.TOKEN_BLOCKED_AT_NEXT_FAILED_SIGNING:
                    this.passwordForm.get('currentPassword').setValue(null);
                    this.passwordForm.get('currentPassword').setErrors({ notCorrect: true });
                    this.showErrorNotification('klant.digicodeHerstel.currentPassword.error.lastTry');
                    break;
                case ErrorCode.MUST_CHANGE_CREDENTIALS:
                    this.usernameForm.get('newUsername').setErrors({ notUnique: true });
                    break;
                case ErrorCode.MUST_CHANGE_PASSWORD:
                    this.passwordForm.get('newPassword').setErrors({ passwordRequirements: true });
                    break;
                default:
                    this.showUnknownErrorNotification(httpErrorResponse.error.code);
            }
        } else if (httpErrorResponse.status === 400) {
            this.passwordForm.get('newPassword').setErrors({ illegalCharacters: true });
        } else {
            this.showErrorNotification('klant.digicodeHerstel.unknown.error');
        }
    }

    public cancel(): void {
        this.toLogin.emit(null);
    }

    private formValid(): boolean {
        const currentUsername = this.username;

        if (this.changeUsername) {
            if (this.usernameForm.get('newUsername').value === currentUsername) {
                this.showErrorNotification('klant.digicodeHerstel.newUsername.error.usernameNotEqualToCurrent');
                return false;
            }

            if (!this.usernameForm.get('newUsername').value.match(this.usernameRegex)) {
                this.usernameForm.get('newUsername').setErrors({ illegalCharacters: true });
                return false;
            }
        }

        if (this.passwordForm.get('currentPassword').value === this.passwordForm.get('newPassword').value) {
            this.showErrorNotification('klant.digicodeHerstel.newPassword.error.passwordsNotEqualToCurrent');
            return false;
        }

        if (this.passwordForm.get('newPassword').value !== this.passwordForm.get('validatePassword').value) {
            this.showErrorNotification('klant.digicodeHerstel.validatePassword.error.passwordsMatch');
            return false;
        }
        if (this.passwordForm.valid && (this.usernameForm ? this.usernameForm.valid : true)) {
            return true;
        }
        if (this.usernameForm) {
            Object.keys(this.usernameForm.controls).forEach((field) => {
                const control = this.usernameForm.get(field);
                control.markAsTouched();
                control.markAsDirty();
            });
        }
        if (this.passwordForm) {
            Object.keys(this.passwordForm.controls).forEach((field) => {
                const control = this.passwordForm.get(field);
                control.markAsTouched();
                control.markAsDirty();
            });
        }
        return false;
    }

    private showErrorNotification(errorContent: string): void {
        this.notification.emit({ status: 'error', message: this.contentService.get(errorContent) });
    }

    private showUnknownErrorNotification(code: string): void {
        this.notification.emit({ status: 'error', message: this.contentService.get('klant.digicodeHerstel.unknown.error.code', { code }) });
    }

    private getChangeDigicodeRequest(): ChangeDigicodeRequest {
        const currentUsername = this.username;
        const currentPassword = this.passwordForm.get('currentPassword').value;
        const newPassword = this.passwordForm.get('newPassword').value;

        if (this.changeUsername) {
            const newUsername = this.usernameForm.get('newUsername').value.trim();
            return { new_username: newUsername, new_password: newPassword, username: currentUsername, password: currentPassword };
        }

        return { new_password: newPassword, username: currentUsername, password: currentPassword };
    }

    private changeDigicode(changeDigicodeRequest: ChangeDigicodeRequest): void {
        this.httpClient
            .post<ChangeDigicodeResponse>(`${this.baseUrl}/digicode/change`, changeDigicodeRequest)
            .pipe(finalize(() => (this.buttonIsDisabled = false)))
            .subscribe({
                next: () => {
                    this.toLogin.emit(this.getDigicodeHerstelNotification());
                },
                error: (error) => this.handleErrorCode(error)
            });
    }

    private changeProspectDigicode(changeDigicodeRequest: ChangeDigicodeRequest): void {
        this.httpClient
            .post<ChangeDigicodeResponse>(`${this.baseUrl}/prospect_digicode/change`, changeDigicodeRequest)
            .pipe(finalize(() => (this.buttonIsDisabled = false)))
            .subscribe({
                next: () => {
                    this.toLogin.emit(this.getDigicodeHerstelNotification());
                },
                error: (error) => this.handleErrorCode(error)
            });
    }

    private getDigicodeHerstelNotification(): DigicodeHerstelNotification {
        if (this.changeUsername) {
            return {
                status: 'success',
                message: this.contentService.get('klant.digicodeHerstel.notification.usernameChangedSuccessfully.text')
            };
        }
        return {
            status: 'success',
            message: this.contentService.get('klant.digicodeHerstel.notification.passwordChangedSuccessfully.text')
        };
    }
}
